import { MILLISECONDS_IN_DAY } from '@stdlib/constants-time';
import * as cookie from 'cookie';

import { JSON_HEADERS, tokenToHeader } from 'app/redux/requests';
import { config } from 'app/redux/store';
import { ApiError } from 'app/typings/error';

import { isApiError } from '../error';

import type { ForgeUrlArgs } from './forgeUrl';
import { forgeUrl } from './forgeUrl';

const COOKIE_TOKEN_KEY = 'user';
const COOKIE_PATH = '/';

export async function apiRequest<Path extends string = string>(
  args: ForgeUrlArgs<Path>
) {
  const response = await apiRequestWithResponse(args);

  const contentType = response.headers.get('Content-Type');

  if (
    contentType?.includes(
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) ||
    contentType?.includes('application/octet-stream') ||
    contentType?.includes('text/csv')
  ) {
    return await response.blob();
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export async function apiRequestWithResponse<Path extends string = string>(
  args: ForgeUrlArgs<Path>
) {
  const token = getCookie(COOKIE_TOKEN_KEY);

  const response = await fetch(
    forgeUrl(config.apiUrl, args),
    token
      ? {
          headers: tokenToHeader(token),
        }
      : undefined
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch ${args.path}`);
  }

  return response;
}

type Method = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function apiMutation<Path extends string = string>(
  method: Method,
  args: ForgeUrlArgs<Path>,
  payload?: unknown
) {
  const token = getCookie(COOKIE_TOKEN_KEY);
  const url = forgeUrl(config.apiUrl, args);

  const headers: HeadersInit = {
    ...(payload ? JSON_HEADERS : {}),
    ...(token ? tokenToHeader(token) : {}),
  };

  const response = await fetch(url, {
    method,
    headers: headers,
    body: payload ? JSON.stringify(payload) : undefined,
  });

  const contentType = response.headers.get('content-type') || '';
  const responseBody = await response.text();

  if (!response.ok) {
    const errorMessage = `Failed to mutate ${url}`;
    let parsedError: unknown = responseBody;

    if (contentType.includes('application/json')) {
      try {
        parsedError = JSON.parse(responseBody);
      } catch {
        // Ignore parse error, keep raw text
      }
    }

    if (parsedError && isApiError(parsedError)) {
      throw new ApiError(errorMessage, response.status, parsedError);
    }

    throw new Error(errorMessage, {
      cause:
        typeof parsedError === 'object'
          ? { ...parsedError, code: response.status }
          : { message: parsedError, code: response.status },
    });
  }

  if (!responseBody) {
    return null;
  }

  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(responseBody);
    } catch {
      return responseBody;
    }
  }

  return responseBody;
}

function getCookie(
  key: string,
  options?: cookie.CookieParseOptions
): string | undefined {
  if (typeof window !== 'undefined') {
    const cookies = cookie.parse(document.cookie, options);

    const cookieValue = cookies[key];

    return typeof cookieValue === 'string' && cookieValue.length > 0
      ? cookieValue
      : undefined;
  }

  return;
}

export function setUserCookie(token: string | undefined) {
  if (!token) {
    return;
  }

  if (typeof window !== 'undefined') {
    document.cookie = cookie.serialize(COOKIE_TOKEN_KEY, token, {
      path: COOKIE_PATH,
      maxAge: 60 * MILLISECONDS_IN_DAY,
    });
  }
}

export function removeUserCookie() {
  if (typeof window !== 'undefined') {
    document.cookie = cookie.serialize(COOKIE_TOKEN_KEY, '', {
      path: COOKIE_PATH,
      expires: new Date(1970, 1, 1, 0, 0, 1),
      maxAge: 0,
    });
  }
}
